/** Independent saved-card section; errors never remove the analytical siblings. */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ShopifyTokenSource } from '../../shopifyApiFetch'
import { insightOpportunityCopy } from '@testfeed/product-language'
import { storeResearchCardsSchema } from '@testfeed/shopify-contracts'
import { researchCardQueryOptions } from '../../queries/researchCardQueries'
import { useShopifyAppContext, buildShopifyAppScopeKey } from '../../ShopifyAppContext'
import { usePageLifecycle } from '../../hooks/usePageLifecycle'
import { useShopifyEntitlements } from '../../hooks/useShopifyEntitlements'
import { useShopifyTrackedMutation } from '../../hooks/useShopifyTrackedMutation'
import { researchCardsPage } from '../../pages/insights.page'
import { shopifyApiClient } from '../../shopifyApiClient'
import { throwMerchantFacingApiError } from '../../shopifyApiError'
import { parseContractPayload } from '../../merchantErrors'
import { trackShopifyRefetch } from '../../shopifyUiTracking'
import { SkeletonBodyText } from '../shims/SkeletonBodyTextShim'
import { ResearchReadingSection, ResearchReadingNotice } from './ResearchReadingSection'

const copy = insightOpportunityCopy.readingPreview
export function SavedResearchCards({ shopify }: { shopify: ShopifyTokenSource }) {
  const scope = useShopifyAppContext()
  const key = buildShopifyAppScopeKey(scope)
  const active = () => !scope.isActiveScope || scope.isActiveScope(key)
  const entitlements = useShopifyEntitlements(shopify, {
    ...scope,
    isTestFeedConnected: Boolean(scope.orgId && scope.connectionId),
  })
  const entitled = !entitlements.isError && entitlements.data.features.research_cards?.state === 'enabled'
  const options = researchCardQueryOptions(shopify, scope, entitled)
  const query = useQuery({ ...options, staleTime: 0 })
  const client = useQueryClient()
  usePageLifecycle(researchCardsPage, `${key}:research`, shopify, { researchCards: query })
  const dismissal = useShopifyTrackedMutation(shopify, 'dismiss-research', {
    mutationFn: async (caseId: string) => {
      if (!active()) throw new Error('RESEARCH_CARD_SCOPE_CHANGED')
      if (!entitled) throw new Error('RESEARCH_CARD_NOT_ENTITLED')
      const response = await shopifyApiClient(shopify).api['research-cards'].dismiss.$post(
        { json: { caseId, dismissed: true } },
        { headers: { 'X-TestFeed-Integration-Connection-Id': scope.connectionId! } },
      )
      if (!response.ok) await throwMerchantFacingApiError(response, 'research-cards')
      const result = parseContractPayload('research-cards', storeResearchCardsSchema, await response.json())
      if (!active() || result.scope.connectionId !== scope.connectionId || result.scope.orgId !== scope.orgId)
        throw new Error('RESEARCH_CARD_SCOPE_CHANGED')
      return result
    },
    onSuccess: (result) => {
      if (active()) client.setQueryData(options.queryKey, result)
    },
  })
  if (!options.enabled) return null
  if (query.isError || dismissal.isError)
    return (
      <s-section heading={copy.staticHeading}>
        <s-paragraph>{copy.failed}</s-paragraph>
        <s-button
          onClick={() => {
            dismissal.reset()
            void trackShopifyRefetch(shopify, () => query.refetch())
          }}
        >
          {copy.retry}
        </s-button>
      </s-section>
    )
  if (query.isPending)
    return (
      <s-section heading={copy.staticHeading}>
        <SkeletonBodyText lines={3} />
      </s-section>
    )
  if (!query.data || query.data.state === 'not_started') return null
  if (query.data.state !== 'ready' && query.data.state !== 'no_match')
    return <ResearchReadingNotice state={query.data.state} />
  return (
    <ResearchReadingSection
      examples={query.data.cards}
      busy={dismissal.isPending}
      onDismiss={(id) => dismissal.mutate(id)}
    />
  )
}
